import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { AlertTriangle, CheckCircle, Info, XCircle } from "lucide-react";

interface ConfirmationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: string;
    confirmText?: string;
    cancelText?: string;
    variant?: "default" | "destructive" | "warning" | "success";
    onConfirm: () => void;
    onCancel?: () => void;
    loading?: boolean;
}

export default function ConfirmationDialog({
    open,
    onOpenChange,
    title,
    description,
    confirmText = "Confirm",
    cancelText = "Cancel",
    variant = "default",
    onConfirm,
    onCancel,
    loading = false
}: ConfirmationDialogProps) {

    const handleCancel = () => {
        onCancel?.();
        onOpenChange(false);
    };

    const handleConfirm = () => {
        onConfirm();
        onOpenChange(false);
    };

    const getIcon = () => {
        switch (variant) {
            case "destructive":
                return <XCircle className="w-6 h-6 text-red-600" />;
            case "warning":
                return <AlertTriangle className="w-6 h-6 text-amber-600" />;
            case "success":
                return <CheckCircle className="w-6 h-6 text-green-600" />;
            default:
                return <Info className="w-6 h-6 text-blue-600" />;
        }
    };

    const getConfirmButtonStyles = () => {
        switch (variant) {
            case "destructive":
                return "bg-red-600 hover:bg-red-700 text-white";
            case "warning":
                return "bg-amber-600 hover:bg-amber-700 text-white";
            case "success":
                return "bg-green-600 hover:bg-green-700 text-white";
            default:
                return "bg-blue-600 hover:bg-blue-700 text-white";
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="confirmation-dialog">
                <DialogHeader className="space-y-4">
                    <div className="flex items-center gap-3">
                        {getIcon()}
                        <DialogTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                            {title}
                        </DialogTitle>
                    </div>
                    <DialogDescription className="text-slate-600 dark:text-slate-400 leading-relaxed">
                        {description}
                    </DialogDescription>
                </DialogHeader>

                <DialogFooter className="gap-2 mt-6">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading}
                        data-testid="button-cancel"
                    >
                        {cancelText}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={loading}
                        className={getConfirmButtonStyles()}
                        data-testid="button-confirm"
                    >
                        {loading ? "Processing..." : confirmText}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}